import { ScopeProvider } from 'dreamstate';
import { memo, useMemo } from 'react';

import { ThemeProvider } from '@vimeo/core/components/ThemeProvider';
import { Id, Maybe, Optional } from '@vimeo/core/types/generic';
import { ScopeErrorBoundary } from '@vimeo/live-core/components/error/scope';
import { ESidebarAttach } from '@vimeo/live-core/components/sidebar';
import { liveQueryConfig } from '@vimeo/live-core/configs/LiveQueryConfig';
import { useInitialRenderTime } from '@vimeo/live-core/hooks/react/useInitialRenderTime';
import { useApplicationTrackingDetailsSync } from '@vimeo/live-core/tracking/helpers/hooks';
import {
  EComposerApplicationType,
  EComposerSessionType,
  EExternalModule,
  TComposerSessionId,
} from '@vimeo/live-core/types/composer_session';
import { EInteractionModule } from '@vimeo/live-core/types/interaction_tools';
import { IInteractionStoreInitialState } from '@vimeo/live-core/types/store';
import { TThemeType } from '@vimeo/live-core/types/theme';
import { getUrlQueryValue } from '@vimeo/live-core/utils/general/url_query';
import { useViewer, Viewer } from '@vimeo/viewer';
import { useIsVpaas } from '@vimeo/vpaas';

import { InteractionToolsAmbient } from './ambient';
import { useInteractionToolsRegistrantUuid, InteractionToolsStatusObserver } from './embeding';
import { InteractionToolsErrorPage } from './error';
import { InteractionToolsProvider } from './InteractionToolsProvider';
import { InteractionToolsRouter } from './InteractionToolsRouter';
import { InteractionToolsFooter } from './layout';

interface IInteractionToolsEntryProps {
  /** Session type of interaction room. */
  sessionType: EComposerSessionType;
  /** Session id of interaction room. */
  sessionId: TComposerSessionId;
  /** Whether the login form can be collapsed. */
  isCollapsibleSignIn?: boolean;
  /** Turn off header rendering for interaction widget. */
  isHeaderEnabled?: boolean;
  /** Turn off footer rendering for interaction widget. */
  isFooterEnabled?: boolean;
  /**
   * Activate logic to switch status and render empty space when chat widget is not active.
   * In this case we reduce firebase load and do not render widget when it is not needed.
   * Turned on by default.
   */
  isActiveStatusObservingEnabled?: boolean;
  /**
   * Whether widget is implemented in application with it own status observing logic.
   * In this case we render widget as is without status observing logic.
   */
  isBypassStatusObservingEnabled?: boolean;
  /**
   * Whether widget should use internal theme and manage it or expect theme context to exist.
   * If `true` is provided, application expects theme supplied from React tree above.
   */
  isCustomThemeEnabled?: boolean;
  /** Turn off management modules rendering for interaction widget. */
  isManagementDisabled?: boolean;
  /** Whether the interaction tools are rendered as standalone application. */
  isStandalone?: boolean;
  /** Room security password. */
  roomPassword?: string;
  /** Hashed room security password. */
  roomHashedPassword?: string;
  /** Room unlisted hash-code. */
  roomUnlistedHash?: Maybe<string>;
  /** Room auth options disabled in current context. */
  roomDisabledOptions?: Maybe<Array<string>>;
  /** Attach position for interaction toolbar. */
  toolbarAttachPosition?: ESidebarAttach;
  /** Allow interaction toolbar panel fill all space. */
  toolbarPanelIsFluid?: boolean;
  /** Set expanded size of the interaction toolbar panel. */
  toolbarPanelMaxSize?: number;
  /** Callback for collapsing panel. */
  toolbarPanelOnClose?: (active: Optional<Id>) => void;
  /** Callback for expanding panel. */
  toolbarPanelOnOpen?: (active: Optional<Id>) => void;
  /**
   * Callback for extending panel content
   * @param isExtended - represents the current extend state
   */
  toolbarPanelOnToggleExtend?: (isExtended: boolean) => void;
  /** The background color for interaction tools. */
  backgroundColor?: string;
}

/**
 * Main entry point for interaction tools embed application.
 * - Used for direct import inside next applications
 */
export const InteractionToolsEntry = memo(function ({
  sessionId,
  sessionType,
  isCollapsibleSignIn = true,
  isActiveStatusObservingEnabled = true,
  isBypassStatusObservingEnabled = false,
  isFooterEnabled = true,
  isCustomThemeEnabled = false,
  isManagementDisabled = false,
  isStandalone = false,
  roomPassword,
  roomHashedPassword,
  roomUnlistedHash,
  roomDisabledOptions,
  toolbarAttachPosition = ESidebarAttach.RIGHT,
  toolbarPanelIsFluid = false,
  toolbarPanelMaxSize,
  toolbarPanelOnClose,
  toolbarPanelOnOpen,
  toolbarPanelOnToggleExtend,
  backgroundColor,
}: IInteractionToolsEntryProps) {
  const isVpaas: boolean = useIsVpaas();
  const viewer: Viewer = useViewer();
  const renderAt: number = useInitialRenderTime();
  const roomRegistrantUuid: Optional<string> = useInteractionToolsRegistrantUuid();

  const initialState: IInteractionStoreInitialState = useMemo(
    () => ({
      renderAt,
      sessionApplicationType: EComposerApplicationType.INTERACTION_TOOLS,
      sessionType: sessionType,
      sessionId: sessionId,
      isVpaas,
      interaction: {
        isStandalone: isStandalone,
        auth: {
          noAutoSignIn: false,
          isCollapsibleSignIn: isCollapsibleSignIn,
          roomPassword: roomPassword,
          roomHashedPassword: roomHashedPassword,
          roomUnlistedHash: roomUnlistedHash,
          roomRegistrantUuid: roomRegistrantUuid,
          roomDisabledOptions: roomDisabledOptions,
        },
        feature: {
          isManagementDisabled: isManagementDisabled,
          module: EInteractionModule.AUTO,
        },
        toolbar: {
          attachPosition: toolbarAttachPosition,
          panelIsFluid: toolbarPanelIsFluid,
          panelMaxSize: toolbarPanelMaxSize,
          panelOnClose: toolbarPanelOnClose,
          panelOnOpen: toolbarPanelOnOpen,
          panelOnToggleExtend: toolbarPanelOnToggleExtend,
        },
        // Get as parameter?
        theme: getUrlQueryValue(liveQueryConfig.INTERACTION.THEME) as TThemeType,
        backgroundColor,
      },
      externalModules: [EExternalModule.FIREBASE],
    }),
    [
      sessionType,
      sessionId,
      isVpaas,
      roomPassword,
      roomHashedPassword,
      roomUnlistedHash,
      roomRegistrantUuid,
      roomDisabledOptions,
      isManagementDisabled,
      toolbarAttachPosition,
      toolbarPanelIsFluid,
      toolbarPanelMaxSize,
      renderAt,
      toolbarPanelOnClose,
      toolbarPanelOnOpen,
      toolbarPanelOnToggleExtend,
      backgroundColor,
      isCollapsibleSignIn,
      isStandalone,
    ],
  );

  useApplicationTrackingDetailsSync({
    sessionApplicationType: initialState.sessionApplicationType,
    sessionType: initialState.sessionType,
    sessionId: initialState.sessionId,
    userId: viewer.user?.id,
    userLocale: viewer?.locale,
  });

  const applicationTree = (
    <ScopeErrorBoundary fallback={InteractionToolsErrorPage}>
      <InteractionToolsProvider initialState={initialState}>
        <InteractionToolsRouter />
        <InteractionToolsAmbient />

        {isFooterEnabled ? <InteractionToolsFooter /> : null}
      </InteractionToolsProvider>
    </ScopeErrorBoundary>
  );

  return (
    <InteractionToolsStatusObserver
      isActive={isActiveStatusObservingEnabled}
      isBypass={isBypassStatusObservingEnabled}
      sessionId={sessionId}
      sessionType={sessionType}
    >
      <ScopeProvider>
        {isCustomThemeEnabled ? applicationTree : <ThemeProvider>{applicationTree}</ThemeProvider>}
      </ScopeProvider>
    </InteractionToolsStatusObserver>
  );
});
